// src/pages/LoraFinetuning/index.js
import React, { useState, useRef } from "react";
import { 
  Button, Card, message, Typography, Space, Divider, Spin, 
  Collapse, Slider, Row, Col, InputNumber, Select, Form, Input, Layout
} from "antd";
import { 
  SendOutlined, SaveOutlined, ClearOutlined, 
  SettingOutlined, DownloadOutlined, CloseOutlined, 
  GithubOutlined, InfoCircleOutlined
} from "@ant-design/icons";

const { TextArea } = Input;
const { Title, Paragraph } = Typography;
const { Option } = Select;
const { Content } = Layout;

const API_BASE_URL = "https://api.xizaijianqishu.top";
const REQUEST_TIMEOUT = 3000000; // 5分钟超时

export default function LoraImageGeneration() {
  const [prompt, setPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [generatedImage, setGeneratedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedBaseModel, setSelectedBaseModel] = useState("stable-diffusion-v1.5");
  const [timeoutError, setTimeoutError] = useState(false);
  
  // 添加参数设置状态
  const [params, setParams] = useState({
    width: 512,                 // 图片宽度
    height: 512,                // 图片高度
    num_inference_steps: 30,    // 推理步数
    guidance_scale: 6.0,        // 引导权重
    seed: 42,                   // 随机种子 (-1表示随机)
  });

  // 请求控制器引用，用于取消请求
  const abortControllerRef = useRef(null);

  // 处理图片生成
  const handleGenerate = async () => {
    if (!prompt?.trim()) {
      message.warning("请输入提示词");
      return;
    }

    // 开始新请求前，清除旧的图片结果和超时错误
    setGeneratedImage(null);
    setTimeoutError(false);
    setLoading(true);
    
    // 创建新的请求控制器
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      // 创建超时Promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error("请求超时，请稍后重试"));
        }, REQUEST_TIMEOUT);
      });

      // 同时进行API请求和超时检查
      const response = await Promise.race([
        fetch(`${API_BASE_URL}/api/image-generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            "Referer": "https://xizaijianqishu.top/lora-finetuning",
          },
          body: JSON.stringify({ 
            prompt,
            negative_prompt: negativePrompt,
            base_model: selectedBaseModel,
            ...params
          }),
          signal, // 添加取消信号
        }),
        timeoutPromise
      ]);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // 确保base64字符串包含正确的前缀
          const base64Image = data.image_base64.startsWith('data:image') ? 
            data.image_base64 : 
            `data:image/png;base64,${data.image_base64}`;
          
          setGeneratedImage(base64Image);
          message.success("图片生成成功！");
        } else {
          message.error(data.message || "图片生成失败");
        }
      } else {
        // 处理HTTP错误
        try {
          const errorData = await response.json();
          message.error(errorData.message || "图片生成失败");
        } catch (e) {
          message.error("图片生成失败");
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        message.info("图片生成已取消");
      } else if (error.message.includes('请求超时')) {
        setTimeoutError(true);
        message.error(error.message);
      } else {
        console.error("图片生成错误:", error);
        message.error("图片生成失败，请稍后重试");
      }
    } finally {
      setLoading(false);
      // 清除请求控制器
      abortControllerRef.current = null;
    }
  };

  // 取消图片生成
  const handleCancelGenerate = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setLoading(false);
    }
  };

  // 处理图片下载
  const handleDownload = () => {
    if (!generatedImage) {
      message.warning("没有可下载的图片");
      return;
    }
    
    try {
      // 从base64字符串创建Blob对象
      const base64Data = generatedImage.replace(/^data:image\/png;base64,/, '');
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      
      // 创建下载链接
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `lora-generated-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // 释放URL对象
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("下载图片失败:", error);
      message.error("下载图片失败");
    }
  };

  // 清空所有内容
  const handleClear = () => {
    // 如果正在生成，先取消
    handleCancelGenerate();
    
    setPrompt("");
    setNegativePrompt("");
    setGeneratedImage(null);
    setTimeoutError(false);
    setParams(prev => ({
      ...prev,
      seed: -1
    }));
  };

  // 参数变化处理函数
  const handleParamChange = (key, value) => {
    setParams(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <Layout style={{ background: 'transparent' }}>
      <Content className="main-wrap" style={{ padding: '20px' }}>
        {/* 项目标题与介绍 */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={1} style={{ color: 'var(--theme-primary)', marginBottom: '16px' }}>
            🎨 Stable Diffusion 图片生成系统
          </Title>
          <Paragraph style={{ maxWidth: '800px', margin: '0 auto', color: 'var(--theme-text)' }}>
            基于Stable Diffusion模型的AI图片生成系统，支持多种模型和参数配置，轻松创作高质量图像
          </Paragraph>
        </div>

      <Card
        className="card-glow"
        style={{ marginBottom: "20px" }}
        title={<span style={{ color: "var(--theme-text)" }}>基础模型选择</span>}
      >
        <Select
          value={selectedBaseModel}
          onChange={setSelectedBaseModel}
          style={{ width: "100%", marginBottom: "20px" }}
          placeholder="选择基础模型"
        >
          <Option value="stable-diffusion-v1.5">Stable Diffusion v1.5</Option>
          <Option value="stable-diffusion-xl-base-1.0">Stable Diffusion XL Base 1.0</Option>
        </Select>
      </Card>

      <Card
        className="card-glow"
        style={{ marginBottom: "20px" }}
        title={<span style={{ color: "var(--theme-text)" }}>提示词设置</span>}
      >
        <Form layout="vertical">
          <Form.Item label="正向提示词">
            <TextArea
              rows={4}
              placeholder="请输入正向提示词，描述您想要生成的内容"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              style={{ color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
            />
          </Form.Item>
          
          <Form.Item label="反向提示词">
            <TextArea
              rows={2}
              placeholder="请输入反向提示词，描述您不想要的内容（如：模糊、变形、低质量等）"
              value={negativePrompt}
              onChange={(e) => setNegativePrompt(e.target.value)}
              style={{ color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
            />
          </Form.Item>
        </Form>
      </Card>

      <Card
        className="card-glow"
        style={{ marginBottom: "20px" }}
        title={<span style={{ color: "var(--theme-text)" }}>生成参数设置</span>}
      >
        <Collapse
          bordered={false}
          defaultActiveKey={['1']}
          items={[
            {
              key: '1',
              label: <><SettingOutlined /> 生成参数</>,
              children: (
                <div style={{ padding: "10px 0" }}>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <div style={{ marginBottom: "5px" }}>图片宽度: {params.width}</div>
                      <Slider
                        min={256}
                        max={1024}
                        step={64}
                        value={params.width}
                        onChange={(value) => handleParamChange('width', value)}
                      />
                      <InputNumber
                        min={256}
                        max={1024}
                        step={64}
                        value={params.width}
                        onChange={(value) => handleParamChange('width', value)}
                        style={{ width: "100%", marginTop: "5px", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: "5px" }}>图片高度: {params.height}</div>
                      <Slider
                        min={256}
                        max={1024}
                        step={64}
                        value={params.height}
                        onChange={(value) => handleParamChange('height', value)}
                      />
                      <InputNumber
                        min={256}
                        max={1024}
                        step={64}
                        value={params.height}
                        onChange={(value) => handleParamChange('height', value)}
                        style={{ width: "100%", marginTop: "5px", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: "5px" }}>推理步数: {params.num_inference_steps}</div>
                      <Slider
                        min={20}
                        max={100}
                        step={5}
                        value={params.num_inference_steps}
                        onChange={(value) => handleParamChange('num_inference_steps', value)}
                      />
                      <InputNumber
                        min={20}
                        max={100}
                        step={5}
                        value={params.num_inference_steps}
                        onChange={(value) => handleParamChange('num_inference_steps', value)}
                        style={{ width: "100%", marginTop: "5px", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: "5px" }}>引导权重: {params.guidance_scale.toFixed(1)}</div>
                      <Slider
                        min={1}
                        max={20}
                        step={0.5}
                        value={params.guidance_scale}
                        onChange={(value) => handleParamChange('guidance_scale', value)}
                      />
                      <InputNumber
                        min={1}
                        max={20}
                        step={0.5}
                        value={params.guidance_scale}
                        onChange={(value) => handleParamChange('guidance_scale', value)}
                        style={{ width: "100%", marginTop: "5px", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: "5px" }}>随机种子: {params.seed === -1 ? "随机" : params.seed}</div>
                      <InputNumber
                        min={-1}
                        max={4294967295}
                        value={params.seed}
                        onChange={(value) => handleParamChange('seed', value)}
                        style={{ width: "100%", marginTop: "5px", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
                        placeholder="输入-1表示随机种子"
                      />
                    </Col>
                  </Row>
                </div>
              ),
            },
          ]}
        />
      </Card>

      <Card
        className="card-glow"
        style={{ marginBottom: "20px" }}
        title={<span style={{ color: "var(--theme-text)" }}>生成控制</span>}
      >
        <Space direction="horizontal" style={{ width: "100%", justifyContent: "space-between" }}>
          <Button
            icon={<ClearOutlined />}
            onClick={handleClear}
            disabled={!prompt && !negativePrompt && !generatedImage && !loading}
          >
            清空
          </Button>
          <Space>
            {loading ? (
              <Button
                type="primary"
                icon={<CloseOutlined />}
                onClick={handleCancelGenerate}
                size="large"
              >
                取消生成
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleGenerate}
                disabled={!prompt?.trim()}
                size="large"
              >
                生成图片
              </Button>
            )}
            {generatedImage && (
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                size="large"
              >
                下载图片
              </Button>
            )}
          </Space>
        </Space>
      </Card>

      {loading ? (
        <Card className="card-glow">
          <div style={{ textAlign: "center", padding: "100px" }}>
            <Spin size="large" tip="正在生成图片，请稍候..." />
            <Paragraph style={{ marginTop: "20px", color: "var(--theme-text)" }}>
              1024x1024图片生成可能需要较长时间，请耐心等待...
            </Paragraph>
          </div>
        </Card>
      ) : timeoutError ? (
        <Card className="card-glow">
          <div style={{ textAlign: "center", padding: "100px" }}>
            <Typography.Text type="danger">图片生成超时</Typography.Text>
            <Paragraph style={{ marginTop: "20px", color: "var(--theme-text)" }}>
              1024x1024图片生成时间较长，建议：<br />
              1. 尝试减小图片尺寸（如512x512）<br />
              2. 减少推理步数<br />
              3. 稍后重试
            </Paragraph>
            <Button 
              type="primary" 
              onClick={handleGenerate}
              style={{ marginTop: "20px" }}
            >
              重新生成
            </Button>
          </div>
        </Card>
      ) : generatedImage ? (
        <Card className="card-glow" title={<span style={{ color: "var(--theme-text)" }}>生成结果</span>}>
          <div style={{ textAlign: "center", padding: "20px" }}>
            <img
              src={generatedImage}
              alt="Generated Image"
              style={{ maxWidth: "100%", maxHeight: "600px", borderRadius: "8px" }}
            />
            <div style={{ marginTop: "20px" }}>
              <Button
                icon={<SaveOutlined />}
                onClick={handleDownload}
                size="large"
              >
                保存图片
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      <Divider />

      <Card className="card-glow">
        <Title level={4} style={{ marginBottom: "10px", color: "var(--theme-text)" }}>使用说明</Title>
        <Paragraph style={{ color: "var(--theme-text)" }}>
          1. 选择基础模型（推荐使用Stable Diffusion v1.5）<br />
          2. 输入正向提示词，详细描述您想要生成的内容<br />
          3. （可选）输入反向提示词，排除不想要的元素<br />
          4. 调整生成参数（图片尺寸、推理步数、引导权重等）<br />
          5. 点击"生成图片"按钮<br />
          6. 等待生成完成后，您可以预览和下载图片<br />
          <br />
          <strong>提示：</strong> 1024x1024图片生成可能需要较长时间，请耐心等待。您可以随时点击"取消生成"按钮中断生成过程。
        </Paragraph>
      </Card>

      {/* 项目链接 */}
      <div style={{ textAlign: 'center' }}>
        <Space wrap>
          <Button 
            type="primary" 
            icon={<GithubOutlined />}
            onClick={() => window.open('https://github.com', '_blank')}
          >
            GitHub仓库
          </Button>
          <Button 
            icon={<InfoCircleOutlined />}
            onClick={() => window.location.href = '/post/1769245392821'}
          >
            项目文档
          </Button>
          <Button 
            onClick={() => window.history.back()}
          >
            返回项目列表
          </Button>
        </Space>
      </div>
    </Content>
  </Layout>
);
}